/**
 * CycleCard Component
 *
 * Displays a single cycle - simple temporal grouper
 * Clicking the card navigates to the cycle's objectives.
 */

import React from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { PencilIcon, TrashIcon, CalendarIcon } from '@heroicons/react/24/outline';
import type { CycleCardProps } from '../cycles.types';
import { formatDateRange } from '../cycles.definitions';

export default function CycleCard({ cycle, onEdit, onDelete, className }: CycleCardProps) {
  const navigate = useNavigate();

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on action buttons
    const target = e.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }
    navigate(`/okr/cycles/${cycle.id}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className={clsx(
        'cursor-pointer rounded-lg border-2 border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md hover:border-blue-400',
        className
      )}
    >
      {/* Header */}
      <h3 className="mb-2 font-montserrat text-xl font-bold text-gray-900">
        {cycle.name}
      </h3>

      {/* Description - full width */}
      {cycle.description && (
        <p className="mb-4 text-sm text-gray-600">{cycle.description}</p>
      )}

      {/* Date Range */}
      <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
        <CalendarIcon className="h-4 w-4" />
        <span className="font-montserrat">{formatDateRange(cycle.startDate, cycle.endDate)}</span>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onEdit?.(cycle)}
          className="flex items-center justify-center gap-2 rounded-lg border border-blue-500 bg-blue-50 px-4 py-2 font-montserrat text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
        >
          <PencilIcon className="h-4 w-4" />
          Editar
        </button>
        <button
          onClick={() => onDelete?.(cycle.id)}
          className="flex items-center justify-center gap-2 rounded-lg border border-red-500 bg-red-50 px-4 py-2 font-montserrat text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
        >
          <TrashIcon className="h-4 w-4" />
          Excluir
        </button>
      </div>
    </div>
  );
}
